import { ICountryData } from "@interfaces";

/**
 * Put Australia at the top of the country list.
 *
 * Nearly everyone booking here is Australian, so it leads rather than sitting
 * under A among 249 alphabetical entries. Applied where the list is fetched
 * rather than in each dropdown, because eight screens read that endpoint — the
 * checkout traveller form, My Account, the profile tabs, the Trip Planner roster
 * — and they should not disagree about the order.
 *
 * Matched on `alpha2`, not `countryCode`: the latter is the numeric ISO code
 * ("036" for Australia), which is also what the dropdowns store as their value.
 *
 * Lives apart from `externalApi` so it can be unit tested without importing
 * axios, whose ESM build Jest cannot parse.
 */
const AUSTRALIA_ALPHA2 = "AU";

export const withAustraliaFirst = (countries: ICountryData[]): ICountryData[] => {
    const index = countries.findIndex((country) => country.alpha2 === AUSTRALIA_ALPHA2);
    // Absent, or already leading: nothing to move.
    if (index <= 0) return countries;
    const reordered = [...countries];
    const [australia] = reordered.splice(index, 1);
    return [australia, ...reordered];
};

/** A country as the shared select component wants it. */
export interface ICountryOption {
    id: string;
    label: string;
    /** Lifted out of alphabetical order, so the list marks it as deliberate. */
    isPriority?: boolean;
}

/**
 * Build the country options for a dropdown, flagging Australia.
 *
 * The flag travels with the option rather than being a prop on each select,
 * because four separate forms build this list and the marker should not depend on
 * every one of them remembering to ask for it.
 */
export const toCountryOptions = (countries: ICountryData[]): ICountryOption[] =>
    withAustraliaFirst(countries).map((country) => ({
        id: country.countryCode,
        label: country.name,
        isPriority: country.alpha2 === AUSTRALIA_ALPHA2,
    }));
